import React from "react";
import Flag from "../../components/Flag";
import { isGoalkeeper } from "../playerStats";

const ATTR_ORDER = ["PAC", "SHO", "PAS", "DRI", "DEF", "PHY"];
const ATTR_LABELS = {
  PAC: "Pace",
  SHO: "Shooting",
  PAS: "Passing",
  DRI: "Dribbling",
  DEF: "Defending",
  PHY: "Physical",
};
const ATTR_TO_DETAIL = {
  PAC: "pace",
  SHO: "shooting",
  PAS: "passing",
  DRI: "dribbling",
  DEF: "defending",
  PHY: "physical",
};
const SUBSTAT_LABELS = {
  pace: { acceleration: "Acceleration", sprintSpeed: "Sprint Speed" },
  shooting: {
    positioning: "Positioning",
    finishing: "Finishing",
    shotPower: "Shot Power",
    longShots: "Long Shots",
    volleys: "Volleys",
    penalties: "Penalties",
  },
  passing: {
    vision: "Vision",
    crossing: "Crossing",
    fkAccuracy: "FK Accuracy",
    shortPassing: "Short Pass",
    longPassing: "Long Pass",
    curve: "Curve",
  },
  dribbling: {
    agility: "Agility",
    balance: "Balance",
    reactions: "Reactions",
    ballControl: "Ball Control",
    dribbling: "Dribbling",
    composure: "Composure",
  },
  defending: {
    marking: "Marking",
    standingTackle: "Stand Tackle",
    slidingTackle: "Slide Tackle",
    interceptions: "Interceptions",
    headingAccuracy: "Heading",
  },
  physical: {
    jumping: "Jumping",
    stamina: "Stamina",
    strength: "Strength",
    aggression: "Aggression",
  },
};

// Goalkeeper main-grid (FIFA-style DIV/HAN/KIC/REF/SPE/POS).
const GK_ATTR_ORDER = ["DIV", "HAN", "KIC", "REF", "SPE", "POS"];
const GK_ATTR_LABELS = {
  DIV: "Diving",
  HAN: "Handling",
  KIC: "Kicking",
  REF: "Reflexes",
  SPE: "Speed",
  POS: "Positioning",
};
const GK_ATTR_TO_DETAIL = {
  DIV: "diving",
  HAN: "handling",
  KIC: "kicking",
  REF: "reflexes",
  SPE: "speed",
  POS: "positioning",
};

function Chevron({ up }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-2.5 h-2.5"
    >
      {up ? (
        <polyline points="6 14 12 8 18 14" />
      ) : (
        <polyline points="6 10 12 16 18 10" />
      )}
    </svg>
  );
}

function FormBadge({ mod }) {
  if (mod?.type === "form") {
    return (
      <span
        title={`Hot form: +${mod.delta} ${mod.stat}`}
        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide
                   text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5"
      >
        <Chevron up /> Form
      </span>
    );
  }
  if (mod?.type === "injury") {
    return (
      <span
        title={`Injury: ${mod.delta} ${mod.stat}`}
        className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide
                   text-red-300 bg-red-500/10 border border-red-500/30 rounded px-1.5 py-0.5"
      >
        <Chevron /> Injury
      </span>
    );
  }
  return null;
}

function Stars({ count, max = 5 }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-xs">
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={i < count ? "text-emerald-400" : "text-zinc-700"}
        >
          ★
        </span>
      ))}
    </span>
  );
}

export default function PlayerCard({ player }) {
  if (!player) {
    return (
      <div
        className="h-full rounded-2xl bg-zinc-900/30 border border-zinc-800 border-dashed
                      grid place-items-center text-zinc-500 text-sm"
      >
        Click a player to see details
      </div>
    );
  }

  const gk = isGoalkeeper(player) && !!player.details?.goalkeeping;
  const mod = player.formMod;

  return (
    <div
      key={player.id}
      className="h-full rounded-2xl bg-zinc-900/60 border border-zinc-800 p-6
                 overflow-y-auto animate-slide-in-right scrollbar-fancy"
    >
      {/* Header — name + meta */}
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-2xl font-bold text-zinc-100 truncate">
            {player.name}
          </div>
          <div className="text-xs text-zinc-400 mt-2 flex items-center gap-1.5 flex-wrap">
            <span>{player.pos}</span>
            <span className="text-zinc-700">·</span>
            <Flag nation={player.nation} className="text-base" />
            <span className="truncate">{player.nation}</span>
            {player.age ? (
              <>
                <span className="text-zinc-700">·</span>
                <span>Age {player.age}</span>
              </>
            ) : null}
            {mod && (
              <>
                <span className="text-zinc-700">·</span>
                <FormBadge mod={mod} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Profile grid */}
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {player.heightCm != null && (
          <div className="flex justify-between">
            <span className="text-zinc-500">Height</span>
            <span className="text-zinc-200 font-semibold">
              {player.heightCm} cm
            </span>
          </div>
        )}
        {player.weightKg != null && (
          <div className="flex justify-between">
            <span className="text-zinc-500">Weight</span>
            <span className="text-zinc-200 font-semibold">
              {player.weightKg} kg
            </span>
          </div>
        )}
        {player.preferredFoot && (
          <div className="flex justify-between">
            <span className="text-zinc-500">Foot</span>
            <span className="text-zinc-200 font-semibold">
              {player.preferredFoot}
            </span>
          </div>
        )}
        {player.jerseyNumber != null && (
          <div className="flex justify-between">
            <span className="text-zinc-500">Jersey</span>
            <span className="text-zinc-200 font-semibold">
              #{player.jerseyNumber}
            </span>
          </div>
        )}
        {player.weakFoot != null && (
          <div className="flex justify-between items-center">
            <span className="text-zinc-500">Weak foot</span>
            <Stars count={player.weakFoot} />
          </div>
        )}
        {player.skillMoves != null && (
          <div className="flex justify-between items-center">
            <span className="text-zinc-500">Skill</span>
            <Stars count={player.skillMoves} />
          </div>
        )}
        {player.intReputation != null && (
          <div className="flex justify-between items-center col-span-2">
            <span className="text-zinc-500">Int. rep</span>
            <Stars count={player.intReputation} />
          </div>
        )}
        {player.workRate && (
          <div className="flex justify-between col-span-2">
            <span className="text-zinc-500">Work rate</span>
            <span className="text-zinc-200 font-semibold">
              {player.workRate}
            </span>
          </div>
        )}
      </div>

      {/* Traits */}
      {player.traits?.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Traits
          </div>
          <div className="flex flex-wrap gap-1.5">
            {player.traits.map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-semibold"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Attribute grid — GK stats for keepers, outfield six for everyone else */}
      {gk ? (
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          {GK_ATTR_ORDER.map((k) => {
            const val = player.details.goalkeeping[GK_ATTR_TO_DETAIL[k]];
            return (
              <div
                key={k}
                className="rounded-xl bg-zinc-900 border border-zinc-800 p-3"
              >
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] font-semibold tracking-widest text-zinc-500">
                      {k}
                    </div>
                    <div className="text-[10px] text-zinc-600">
                      {GK_ATTR_LABELS[k]}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-zinc-100 tabular-nums leading-none">
                    {val ?? "–"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          {ATTR_ORDER.map((k) => {
            const detailKey = ATTR_TO_DETAIL[k];
            const subStats = player.details?.[detailKey];
            const hasSubStats =
              subStats && Object.values(subStats).some((v) => v != null);
            const mt = mod?.stat === k ? mod.type : null;
            return (
              <div
                key={k}
                className="rounded-xl bg-zinc-900 border border-zinc-800 p-3"
              >
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] font-semibold tracking-widest text-zinc-500">
                      {k}
                    </div>
                    <div className="text-[10px] text-zinc-600">
                      {ATTR_LABELS[k]}
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-bold tabular-nums leading-none ${
                      mt === "form"
                        ? "text-emerald-400"
                        : mt === "injury"
                          ? "text-red-400"
                          : "text-zinc-100"
                    }`}
                  >
                    {player.attrs[k]}
                  </div>
                </div>
                {hasSubStats && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1">
                    {Object.entries(subStats).map(([key, val]) =>
                      val != null ? (
                        <div
                          key={key}
                          className="flex items-center justify-between text-[10px]"
                        >
                          <span className="text-zinc-500 truncate">
                            {SUBSTAT_LABELS[detailKey]?.[key] || key}
                          </span>
                          <span className="text-zinc-300 tabular-nums font-semibold ml-2 shrink-0">
                            {val}
                          </span>
                        </div>
                      ) : null,
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Positions */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          Positions
        </div>
        <div className="flex flex-wrap gap-2">
          {(player.positions || [player.pos]).map((p) => (
            <span
              key={p}
              className="px-2.5 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-xs font-semibold text-zinc-300"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
